import React, { Dispatch, SetStateAction, useMemo } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import { OpenAssignBoxModal, OpenSporetestModal, SporetestingResult } from '../types';
import { Box, useTheme } from '@mui/material';
import { Biobox, ComplianceStatus } from '../../../../types';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import CompliancePlip from '../../../../components/CompliancePlip';
import { standardDateFormat } from '../../../../helpers/date';

interface UnassignedMesaSporetestsProps {
  bioboxesById: Map<number, Biobox>;
  unassignedTestResults: SporetestingResult[];
  setOpenAssignBioboxModal: Dispatch<SetStateAction<OpenAssignBoxModal>>;
  setOpenSporetestModal: Dispatch<SetStateAction<OpenSporetestModal>>;
}

const UnassignedMesaSporetests: React.FC<UnassignedMesaSporetestsProps> = ({
  bioboxesById,
  unassignedTestResults,
  setOpenAssignBioboxModal,
  setOpenSporetestModal,
}) => {
  const theme = useTheme();

  const getComplianceColor = (result: string) => {
    if (result === 'pass') {
      return ComplianceStatus.Ok;
    } else if (result === 'invalid' || result === 'fail') {
      return ComplianceStatus.Failed;
    }
    return ComplianceStatus.Inactive;
  };

  const getBioboxNumber = (bioboxId: number) => {
    const boxNumber = bioboxesById.get(bioboxId)?.box_number
    return boxNumber
  }

  const handleOpenSporeTestModal = (sporetest: SporetestingResult) => {
    setOpenSporetestModal({
      open: true,
      autoclaveId: null,
      testType: sporetest.test_type,
      sporetest,
    })
  };

  const handleSelectedBiobox = (bioboxId: number) => {
    const biobox = bioboxesById.get(bioboxId)
    if (biobox) setOpenAssignBioboxModal({ open: true, autoclave: null, biobox })
  }

  const testsByBioboxId = useMemo(() => {
    const testsByBox = new Map<number, SporetestingResult[]>()

    unassignedTestResults.forEach(testResult => {
      const boxId = Number(testResult.box_id)

      if (bioboxesById.has(boxId)) {
        testsByBox.get(boxId)?.push(testResult) ?? testsByBox.set(boxId, [testResult])
      }
    })

    return testsByBox
  }, [unassignedTestResults])

  const bioboxIds = [...testsByBioboxId.keys()]

  return (
    <>
      {bioboxIds.map(bioboxId => (
        <Box key={bioboxId}>
          <Box
            sx={{
              fontSize: '13px',
              p: 2,
              backgroundColor: theme.palette.backgroundDanger.main,
              color: theme.palette.dangerDark.main
            }}
          >
            Box# {getBioboxNumber(bioboxId)} needs to be assigned to an autoclave for proper logging. <span onClick={() => handleSelectedBiobox(bioboxId)} className="pointer underline">Assign Box</span>
          </Box>
          <DataGrid
            sx={{
              borderLeft: 'none',
              borderRight: 'none',
              borderBottom: 'none',
              borderRadius: '0',
              color: theme.palette.color50555b.main,
              [`& .${gridClasses.cell}`]: {
                py: 1
              }
            }}
            rows={testsByBioboxId.get(bioboxId) ?? []}
            getRowId={row => row.test_result_id}
            columns={[
              {
                field: 'test_result',
                headerName: '',
                width: 50,
                sortable: false,
                renderCell: (params) => {
                  const status = getComplianceColor(params.row.test_result ?? '');

                  return (
                    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.5 }}>
                      <CompliancePlip message={params.row.test_result ?? ''} status={status} />
                    </Box>
                  );
                }
              },
              {
                field: 'result_description',
                headerName: 'Result',
                flex: 1,
                minWidth: 450,
                renderCell: (params) => <Box>{params.row.result_description ?? params.row.test_result}</Box>
              },
              {
                field: 'test_number',
                headerName: 'Test Number',
                flex: 1,
                minWidth: 135,
                renderCell: (params) => <Box>{params.row.test_number ?? ''}</Box>
              },
              {
                field: 'sample_date',
                headerName: 'Test Date',
                flex: 1,
                minWidth: 125,
                renderCell: (params) => standardDateFormat(params.row.sample_date ?? '')
              },
              {
                field: 'received_date',
                headerName: 'Received Date',
                flex: 1,
                minWidth: 125,
                renderCell: (params) => standardDateFormat(params.row.received_date ?? '')
              },
              {
                field: 'test_date',
                headerName: 'Result Date',
                flex: 1,
                minWidth: 125,
                renderCell: (params) => standardDateFormat(params.row.test_date ?? '')
              },
              {
                field: 'test_operator',
                headerName: 'Operator',
                flex: 1,
                minWidth: 125,
                renderCell: (params) => <Box sx={{ display: 'flex', alignItems: 'center' }}>{params.row.test_operator ?? 'Unknown'}</Box>
              },
              {
                field: 'test_result_id',
                headerName: 'Edit',
                width: 50,
                sortable: false,
                renderCell: (params) => (
                  <Box onClick={() => handleOpenSporeTestModal(params.row)} sx={{ display: 'flex', justifyContent: 'center', cursor: 'pointer' }}>
                    {(params.row.test_type === 'inoffice' || params.row.test_type === 'mesa') && <EditIcon fontSize="small" />}
                  </Box>
                )
              }
            ]}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 10
                }
              },
              sorting: {
                sortModel: [
                  {
                    field: `test_date`,
                    sort: 'desc'
                  }
                ]
              }
            }}
            pageSizeOptions={[5, 10, 20, 25, 50, 100]}
            density="compact"
            getRowHeight={() => 'auto'}
          />
        </Box>
      ))}
    </>
  );
};

export default UnassignedMesaSporetests;
